import React, { useState } from 'react'
import { useTranslation } from '../../../../common/hooks/helper/useTranslation'
import { Box, Stack, Typography } from '@mui/material'
import { useUserType } from '../../../../common/hooks/useUserType'
import { treatmentChore, UserType } from '../../../../models/graphqlTypes'
import { TreatmentsEmptyState } from './TreatmentsEmptyState'
import { useDoctorPatientTreatmentsData } from '../hooks/useDoctorPatientTreatmentsData'
import { Loading } from '../../../../common/components/Loading'
import { TreatmentList } from './TreatmentList/TreatmentList'
import { AddTreatmentButton } from './AddTreatmentButton'
import { BetmenListFilter } from '../../../../common/components'
import { TreatmentListFilter } from '../types/treatments.types'
import { BooleanFilter } from '../../../../common/components/Filters/BooleanFilter'
import { NoFilterResultContentComponent } from './TreatmentPanel/NoFilterResultContent'

type Props = {
  patientId: string
}

const TreatmentSelector: React.FC<React.PropsWithChildren<Props>> = ({
  patientId,
}) => {
  const { t } = useTranslation()
  const [upserting, setUpserting] = useState<treatmentChore | boolean>(false)

  const [filterInput, setFilterInput] = useState<TreatmentListFilter>({})

  const userType = useUserType()

  const {
    activeTreatments,
    deletedTreatments,
    hasAnyTreatment,
    hasFilterResult,
    hasActiveTreatment,
    hasDeletedTreatment,
    loading,
  } = useDoctorPatientTreatmentsData({ filterInput })

  const isDoctor = userType === UserType.Doctor

  return (
    <Box height="100%" width="100%">
      <Stack direction="row" p={2} pb={1} gap={1} alignItems="center">
        <BetmenListFilter
          searchTerm={filterInput.searchTerm}
          handleSearch={(searchTerm) => {
            setFilterInput((prevFilter) => ({
              ...prevFilter,
              searchTerm: searchTerm ?? '',
            }))
          }}
        />

        <BooleanFilter
          filterInput={!!filterInput.isOwn}
          setFilterInput={(newFilter: boolean) =>
            setFilterInput((prevInput) => ({ ...prevInput, isOwn: newFilter }))
          }
          label={t('survey:question.filter.own')}
        />

        {isDoctor && (
          <AddTreatmentButton
            upserting={upserting}
            setUpserting={setUpserting}
            patientId={patientId}
          />
        )}
      </Stack>

      {loading && (
        <Box display="flex" height="100%">
          <Loading />
        </Box>
      )}
      {!loading && (
        <>
          {hasActiveTreatment && (
            <>
              <Typography variant="h6" mx={4}>
                {t('treatment:doctor.patientsActiveTreatmentPlans')}
              </Typography>
              <TreatmentList
                treatments={activeTreatments}
                loading={loading}
                patientId={patientId}
                onEdit={setUpserting}
              />
            </>
          )}

          {hasDeletedTreatment && (
            <>
              <Typography variant="h6" mx={4} mt={4}>
                {t('treatment:doctor.patientsClosedTreatmentPlans')}
              </Typography>
              <TreatmentList
                treatments={deletedTreatments}
                loading={loading}
                patientId={patientId}
              />
            </>
          )}
        </>
      )}

      {!loading && !hasAnyTreatment && (
        <Box
          display="flex"
          width="100%"
          alignItems="center"
          justifyContent="center"
        >
          <TreatmentsEmptyState patientId={patientId} />
        </Box>
      )}

      {!loading && !hasFilterResult && (
        <NoFilterResultContentComponent
          filterValue={filterInput.searchTerm ?? ''}
        />
      )}
    </Box>
  )
}

export { TreatmentSelector }
